// @flow
import * as React from 'react';
import {
    Calendar, Box, Button, Layer, Stack, MaskedInput, ThemeContext,
} from 'grommet';
import { Previous, Next, Calendar as CalendarIcon } from 'grommet-icons';
import { memoize } from 'lodash';
import moment from 'moment';
import { defineMessages } from 'react-intl';
import { withTheme } from 'styled-components';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';
import isValid from 'date-fns/isValid';
import memoizeOne from 'memoize-one';
import * as Yup from 'yup';
import FormStylesContext from 'speed-js-react-web/src/context/FormStylesContext';
import FieldWrapper from '../FieldWrapper';
import MonthPicker from './MonthPicker';
import YearPicker from './YearPicker';
import StyledCloseButton from '../StyledCloseButton';

// see https://stackoverflow.com/questions/11526504/minimum-and-maximum-date
export const MAX_TIMESTAMP = 8640000000000000;
export const MIN_TIMESTAMP = -8640000000000000;

type Props = {
    field: {
        onChange: (e: SyntheticInputEvent<number>) => void,
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        value: any;
        name: string;
    },
    form: {
        setFieldValue: (string, any) => void,
        setTouched: (fields: { [field: string]: boolean }, shouldValidate?: boolean) => void
    },
    required: boolean,
    label: string,
    help_text: string,
    displayFormat?: string,
    valueFormat?: string,
    // eslint-disable-next-line react/no-unused-prop-types
    mapFormatRegex?: string,
    years_span?: number,
    min?: string,
    max?: string,
    theme: Object,
}

type State = {
    calendarVisible: boolean,
    displayValue: string,
    reference: string,
}

const messages = defineMessages({
    required: {
        id: 'datepicker-required',
        defaultMessage: 'This field is required.',
    },
    invalidDate: {
        id: 'datepicker-invalid-date',
        defaultMessage: 'The entered date is invalid.',
    },
    minDateValidation: {
        id: 'datepicker-min-date-validation',
        defaultMessage: 'The entered date should be after {minDate}',
    },
    maxDateValidation: {
        id: 'datepicker-max-date-validation',
        defaultMessage: 'The entered date should be before {maxDate}',
    },
});

@withTheme
class DatePicker extends React.Component<Props, State> {
    calendar: {
        current: Calendar,
    };

    toMoment: (date: string) => moment;

    theme: Object;

    static contextType = FormStylesContext;

    customZindex = {
        layer: {
            zIndex: '1002',
        },
    };

    static defaultProps = {
        displayFormat: 'DD.MM.YYYY',
        valueFormat: 'YYYY-MM-DD',
        mapFormatRegex: /(\w+|\d+)/,
        years_span: undefined,
        min: new Date(MIN_TIMESTAMP).toISOString(),
        max: new Date(MAX_TIMESTAMP).toISOString(),
    };

    static getValidationSchema(field: { validation: Object }) {
        let errMsg = Yup.date().typeError(messages.invalidDate).nullable();
        if (field.validation.required) {
            errMsg = errMsg.required(messages.required).nullable();
        }

        if (field.ui.min) {
            let [min] = [field.ui.min];
            if (min === 'today') {
                min = new Date();
                min.setHours(0);
                min.setMinutes(0);
                min.setSeconds(0);
                min.setMilliseconds(0);
            }

            errMsg = errMsg.min(min, {
                ...messages.minDateValidation,
                values: { minDate: moment(min).format('llll') },
            });
        }

        if (field.ui.max) {
            errMsg = errMsg.max(field.ui.max, {
                ...messages.maxDateValidation,
                values: { minDate: moment(new Date(field.ui.min)).format('LL') },
            });
        }

        return errMsg;
    }

    constructor(props: Props) {
        super(props);
        const { theme: { calendar: calendarTheme } } = props;

        this.calendar = React.createRef();
        this.toMoment = memoize(moment);

        this.getMask = memoize(this.getMask);

        this.getDateFromValue = memoizeOne(this.getDateFromValue);

        this.state = {
            reference: moment().format(),
            calendarVisible: false,
            displayValue: props.field.value ? moment(props.field.value).format(props.displayFormat) : '',
        };

        const extendCalendarTheme = calendarTheme || {};
        this.theme = {
            layer: {
                zIndex: 10,
            },
            ...extendCalendarTheme,
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.field.value !== this.props.field.value) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                displayValue: this.props.field.value
                    ? moment(this.props.field.value).format(this.props.displayFormat)
                    : '',
            });
        }
    }

    setDate = (value: string) => {
        const { displayFormat } = this.props;
        this.setState({
            displayValue: moment(value).format(displayFormat),
        });
    }

    getMask = (format: string) => {
        const date = moment().format(format);

        let { mapFormatRegex } = this.props;
        mapFormatRegex = mapFormatRegex || DatePicker.defaultProps.mapFormatRegex;

        const placeholders = ['DD', 'MM', 'YYYY'];

        const values = (date.match(new RegExp(mapFormatRegex, 'g')) || []).map((val, i) => ({
            regexp: mapFormatRegex,
            placeholder: placeholders[i],
            length: [1, val.length],
        }));
        const delimiters = date.replace(new RegExp(mapFormatRegex, 'g'), '').split('').map(
            (d) => ({ fixed: d }),
        );

        return values.map((v, i) => (
            [v, delimiters[i]]
        )).reduce((a, b) => a.concat(b)).filter((m) => !!m);
    };

    getDateFromValue = (value: string | null) => {
        if (!value) {
            return new Date();
        }

        return parseISO(value);
    }

    handleChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
        const { value } = e.target;
        this.setState({
            displayValue: value || '',
        });
    };

    handleBlur = (e: SyntheticInputEvent<HTMLInputElement>) => {
        const {
            form: { setTouched, setFieldValue }, field: { name }, min: _min, max, valueFormat, displayFormat,
        } = this.props;
        let min = _min;
        if (min === 'today') {
            min = new Date();
            min.setHours(0);
            min.setMinutes(0);
            min.setSeconds(0);
            min.setMilliseconds(0);
        }
        setTouched({ [name]: true });
        const { value } = e.target;
        if (!value) {
            return;
        }
        const parsedDate = parse(value, 'dd.MM.yyyy', new Date());
        if (isValid(parsedDate) && value.split('.')[1].length === 2 && value.split('.')[2].length === 4) {
            const momentValue = moment(value, displayFormat);
            let validValue = momentValue.format(valueFormat);
            if (max && moment(max).isBefore(momentValue)) {
                validValue = moment(max).format(valueFormat);
            }
            if (min && moment(min).isAfter(momentValue)) {
                validValue = moment(min).format(valueFormat);
            }
            setFieldValue(name, validValue);
        } else {
            setFieldValue(name, null);
            this.setState({
                displayValue: '',
            });
        }
    };

    handleSelect = (longVal: string) => {
        const {
            form: { setFieldValue }, field: { name }, valueFormat, displayFormat,
        } = this.props;
        const value = moment(longVal).format(valueFormat);
        setFieldValue(name, value);

        this.setState({
            calendarVisible: false,
            displayValue: moment(value).format(displayFormat),
        });
    };

    handleMonthChange = (month: number) => {
        const { reference } = this.state;
        const currentDate = moment(reference);

        this.setState({
            reference: moment({ year: currentDate.year(), month }).format(),
        });
    };

    handleYearChange = (year: number) => {
        const { reference } = this.state;
        const currentDate = moment(reference);

        this.setState({
            reference: moment({ year, month: currentDate.month() }).format(),
        });
    };

    handleClose = () => {
        this.setState({
            calendarVisible: false,
        });
    };

    handleOpen = () => {
        this.setState({
            calendarVisible: true,
        });
    };

    onClear = () => {
        const { form: { setFieldValue }, field: { name } } = this.props;
        setFieldValue(name, null);
        this.setState({ displayValue: '' });
    };

    renderHeader = ({
        onPreviousMonth,
        onNextMonth,
        previousInBound,
        nextInBound,
    }: {
        date: Date,
        onPreviousMonth: ()=> void,
        onNextMonth: ()=> void,
        previousInBound: boolean,
        nextInBound: boolean,
    }) => {
        const {
            field: { value }, min: _min,
        } = this.props;

        let min = _min;
        if (min === 'today') {
            min = new Date();
            min.setHours(0);
            min.setMinutes(0);
            min.setSeconds(0);
            min = min.toISOString();
        }

        const date = this.getDateFromValue(value);
        return (
            <Box direction="row" align="center" justify="between">
                <Button disabled={!previousInBound} onClick={onPreviousMonth} margin={{ horizontal: 'small' }}>
                    <Box>
                        <Previous />
                    </Box>
                </Button>
                <Box width="50%">
                    <MonthPicker
                        value={date.getMonth()}
                        onChange={this.handleMonthChange}
                        dateValue={date}
                        minDate={min}
                        maxDate={this.props.max}
                    />
                </Box>
                <Box width="40%">
                    <YearPicker
                        value={date.getFullYear()}
                        onChange={this.handleYearChange}
                        yearsSpan={this.props.years_span}
                        minYear={new Date(min).getFullYear()}
                        maxYear={new Date(this.props.max).getFullYear()}
                    />
                </Box>
                <Button disabled={!nextInBound} onClick={onNextMonth} margin={{ horizontal: 'small' }}>
                    <Box>
                        <Next />
                    </Box>
                </Button>
            </Box>
        );
    };

    render() {
        const {
            field: { name, value },
            label,
            help_text: help,
            required,
            displayFormat,
            min: _min,
            max,
        } = this.props;

        let min = _min;
        if (min === 'today') {
            min = new Date();
            min.setHours(0);
            min.setMinutes(0);
            min.setSeconds(0);
            min.setMilliseconds(0);
        }

        const { calendarVisible, reference, displayValue } = this.state;
        const bounds = [min, max];
        return (
            <FieldWrapper
                name={name}
                required={required}
                helpText={help}
                label={label}
            >
                <Stack anchor="right">
                    <MaskedInput
                        mask={this.getMask(displayFormat)}
                        name={name}
                        style={this.context[name]}
                        onChange={this.handleChange}
                        onBlur={this.handleBlur}
                        value={displayValue}
                        id={`id-${name}`}
                    />
                    <Box direction="row">
                        {(value && displayValue) && <StyledCloseButton onClick={this.onClear} plain />}
                        <Button style={{ padding: '4px' }} icon={<CalendarIcon />} onClick={this.handleOpen} />
                    </Box>
                </Stack>
                {
                    calendarVisible && (
                        <ThemeContext.Extend value={this.theme}>
                            <Layer
                                position="center"
                                modal
                                onClickOutside={this.handleClose}
                                onEsc={this.handleClose}
                            >
                                <Box pad="small">
                                    <Calendar
                                        date={value}
                                        firstDayOfWeek={1}
                                        header={this.renderHeader}
                                        ref={this.calendar}
                                        onSelect={this.handleSelect}
                                        animate={false}
                                        locale={moment.locale()}
                                        reference={reference}
                                        daysOfWeek
                                        bounds={bounds}
                                    />
                                </Box>
                            </Layer>
                        </ThemeContext.Extend>
                    )
                }
            </FieldWrapper>
        );
    }
}


export default DatePicker;
